import apiClient from '../utils/apiClient';
import mockFixtures from '../mocks/mockFixtures';

const MOCK_DELAY_MS = 250;

const normalizeCriteria = (criteria) => ({
  logic: criteria.logic,
  conditions: criteria.conditions.map((condition) => (
    Array.isArray(condition.conditions)
      ? normalizeCriteria(condition)
      : {
          attributeId: condition.attributeId.trim(),
          operator: condition.operator.trim(),
          value: condition.value.trim(),
        }
  ))
});

/**
 * Set REACT_APP_AUTH_MODE=bypass or REACT_APP_API_MODE=mock to use local mocks.
 * Add future mock routes to this registry without changing page components.
 */
const mockRoutes = [
  {
    method: 'get',
    matches: (url) => url.includes('/dropdown/connect-instances'),
    respond: async () => ({ status: 'ok', instances: mockFixtures.instances }),
  },
  {
    method: 'post',
    matches: (url) => url.includes('/dropdown/queues'),
    respond: async ({ data }) => ({
      status: 'ok',
      queues: mockFixtures.queuesByInstance[data?.instance_id] || []
    }),
  },
  {
    method: 'post',
    matches: (url) => url.includes('/dropdown/routing-profiles'),
    respond: async ({ data }) => ({ 
      status: 'ok', 
      routing_profiles: mockFixtures.routingProfilessByInstance[data?.instance_id] || [] }),
  },
  {
    method: 'post',
    matches: (url) => url.includes('/dropdown/security-profiles'),
    respond: async ({ data }) => ({ 
      status: 'ok', 
      security_profiles: mockFixtures.securityProfilessByInstance[data?.instance_id] || [] }),
  },
  {
    method: 'post',
    matches: (url) => url.includes('/dropdown/hierarchy-groups'),
    respond: async ({ data }) => ({ 
      status: 'ok', 
      hierarchy_groups: mockFixtures.hierarchyGroupIdsByInstance[data?.instance_id] || [] }),
  },
  {
    method: 'post',
    matches: (url) => url.includes('/dropdown/simulations'),
    respond: async ({ data }) => ({ 
      status: 'ok', 
      simulations: mockFixtures.routingSimulationsByInstance[data?.instanceId] || [] }),
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/view-agents'),
    respond: async ({ data }) => {
      const agents = mockFixtures.agentsByInstance[data?.instance_id] || [];
      return {
        status: 'ok',
        result: {
          instance_id: data?.instance_id,
          agent_count: agents.length,
          agents,
          include_details: data?.include_details !== false,
          pages_read: 1,
        },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/view-profiles-queues'),
    respond: async ({ data }) => {
      if (!data?.instance_id) {
        return { status: 'error', error: 'missing_instance_id', message: 'instance_id is required' };
      }
      const instanceId = data.instance_id;
      const instanceArn = `arn:aws:connect:us-east-1:123456789012:instance/${instanceId}`;
      const instanceQueues = mockFixtures.queuesByInstance[instanceId] || [];
      const profiles = (mockFixtures.routingProfilessByInstance[instanceId] || []).map((profile, index) => {
        const queue = instanceQueues[index];
        const queues = queue ? [{
          ...queue,
          arn: `${instanceArn}/queue/${queue.id}`,
          channel: 'VOICE',
          priority: 1,
          delay: 0,
        }] : [];
        return {
          ...profile,
          arn: `${instanceArn}/routing-profile/${profile.id}`,
          queue_count: queues.length,
          queues,
        };
      });
      return {
        status: 'ok',
        result: { instance_id: instanceId, profile_count: profiles.length, profiles },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/update-agent-routing-profile'),
    respond: async ({ data }) => {
      const { instanceId, userId, routingProfileId } = data || {};
      if (!instanceId || !userId || !routingProfileId) {
        return { status: 'error', message: 'Missing required fields: instanceId, userId, routingProfileId' };
      }
      const agents = mockFixtures.agentsByInstance[instanceId] || [];
      const agent = agents.find((a) => a.id === userId);
      if (!agent) {
        return { status: 'error', message: 'Agent not found' };
      }
      agent.routing_profile_id = routingProfileId;
      return { status: 'ok', result: { instanceId, userId, routingProfileId } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/agent-activity'),
    respond: async ({ data }) => {
        const rows = mockFixtures.agentActivityByInstance[data?.instance_id] || [];
      return {
        status: 'ok',
        result: {
          instance_id: data?.instance_id,
            agent_count: rows.length,
            rows,
        },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/queue-performance'),
    respond: async ({ data }) => {
      const rows = mockFixtures.queuePerformanceByInstance[data?.instance_id] || [];
      return {
        status: 'ok',
        result: {
          instance_id: data?.instance_id,
          queue_count: rows.length,
          rows,
        },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/view-attributes'),
    respond: async ({ data }) => {
      const attributes = mockFixtures.attributesByInstance[data?.instance_id] || [];
      return {
        status: 'ok',
        result: {
          instance_id: data?.instance_id,
          attribute_count: attributes.length,
          attributes,
        },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/view-routing-groups'),
    respond: async ({ data }) => {
      const allRoutingGroups = mockFixtures.routingGroupsByInstance[data?.instanceId] || [];
      // Filter out soft-deleted routing groups
      const routingGroups = allRoutingGroups.filter((rg) => !rg.deleted_at);
      return {
        status: 'ok',
        result: {
          instanceId: data?.instanceId,
          routingGroup_count: routingGroups.length,
          routingGroups,
          include_details: data?.include_details !== false,
          pages_read: 1,
        },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/view-routing-policies'),
    respond: async ({ data }) => {
      const allRoutingPolicies = mockFixtures.routingPoliciesByInstance[data?.instanceId] || [];
      const routingPolicies = allRoutingPolicies.filter((rp) => !rp.deleted_at);
      return {
        status: 'ok',
        result: {
          instanceId: data?.instanceId,
          routingPolicies: routingPolicies
        },
      };
    },
  },
    {
    method: 'post',
    matches: (url) => url.includes('/admin/view-routing-restriction'),
      respond: async ({ data }) => {
        const allRoutingRestrictions = mockFixtures.routingRestrictionByInstance[data?.instanceId] || [];
        const routingRestrictions = allRoutingRestrictions.filter((restriction) => !restriction.deleted_at);
        return {
          status: 'ok',
          result: {
            instanceId: data?.instanceId,
            routingRestrictions
          }
        };
      },
  },
  {
    method: 'post',
    matches: (url) => (
      url.includes('/admin/routing-simulation') &&
      !url.includes('/admin/routing-simulation-registry')
    ),
    respond: async ({ data }) => {
      if (data?.simulationId) {
        if (!Object.prototype.hasOwnProperty.call(data, 'activeRestrictions')) {
          const storedSimulation = mockFixtures.routingSimulationsByInstance[data?.instanceId]
            ?.find((simulation) => simulation.simulationId === data.simulationId);
          if (!storedSimulation) {
            return { status: 'error', message: 'Routing simulation not found' };
          }
          return { status: 'ok', result: { simulation: storedSimulation } };
        }

        const simulation = mockFixtures.routingSimulationResultById[data.simulationId]?.[0];
        if (!simulation) {
          return { status: 'error', message: 'Routing simulation not found' };
        }

        return { status: 'ok', result: { simulation } };
      }

      const simulations = mockFixtures.routingSimulationsByInstance[data?.instanceId] || [];
      return {
        status: 'ok',
        result: {
          instanceId: data?.instanceId,
          simulation_count: simulations.length,
          simulations,
        },
      };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/routing-simulation-registry'),
    respond: async ({ data }) => {
      const simulations = mockFixtures.routingSimulationsByInstance[data?.instanceId];
      if (!simulations) return { status: 'error', message: 'Connect instance not found' };

      const routingPolicy = mockFixtures.routingPoliciesByInstance[data.instanceId]
        ?.find((policy) => policy.policyId === data.routingPolicyId);
      const restriction = mockFixtures.routingRestrictionByInstance[data.instanceId]
        ?.find((item) => item.restrictionId === data.restrictionId);
      const simulation = {
        simulationId: data.simulationId.trim(),
        simulationName: data.simulationName.trim(),
        instanceId: data.instanceId,
        routingPolicyId: data.routingPolicyId,
        routingPolicyName: routingPolicy?.policyId || data.routingPolicyId,
        restrictionId: data.restrictionId,
        restrictionName: restriction?.restrictionName || data.restrictionId,
        contactAttributes: data.contactAttributes,
        active: data.active
      };
      simulations.push(simulation);
      return { status: 'ok', result: { simulation } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/attribute-registry'),
    respond: async ({ data }) => {
      const attributes = mockFixtures.attributesByInstance[data?.instanceId];
      if (!attributes) {
        return { status: 'error', message: 'Connect instance not found' };
      }

      const duplicate = attributes.some(
        (attribute) => (
          attribute.attributeId.toLowerCase() === data?.attributeId?.trim().toLowerCase() ||
          attribute.attributeName.toLowerCase() === data?.attributeName?.trim().toLowerCase()
        )
      );
      if (duplicate) {
        return { status: 'error', message: 'An attribute with this ID or name already exists' };
      }

      const attribute = {
        instanceId: data.instanceId,
        attributeId: data.attributeId.trim(),
        attributeName: data.attributeName.trim(),
        displayName: data.displayName.trim(),
        dataType: data.dataType,
        allowedOperators: data.allowedOperators || {},
        allowedValues: data.allowedValues.map((allowedValue) => ({
          value: allowedValue.value.trim(),
          displayName: allowedValue.displayName.trim(),
        })),
        active: data.active,
      };
      attributes.push(attribute);

      return { status: 'ok', result: { attribute } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/routing-policy-registry'),
    respond: async ({ data }) => {
      const routingPolicies = mockFixtures.routingPoliciesByInstance[data?.instanceId];
      if (!routingPolicies) return { status: 'error', message: 'Connect instance not found' };

      const routingPolicy = {
        policyId: data.policyId.trim(),
        policyName: data.policyName.trim(),
        instanceId: data.instanceId,
        routingSteps: data.routingSteps.map((step) => ({
          step: Number(step.priority),
          routingGroupId: step.routingGroupId,
          timeoutSeconds: step.timeoutSeconds == null ? null : Number(step.timeoutSeconds)
        })),  
        status: data.status || 'Inactive',
      };
      routingPolicies.push(routingPolicy);
      return { status: 'ok', result: { routingPolicy } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/routing-restriction-registry'),
    respond: async ({ data }) => {
      const restrictions = mockFixtures.routingRestrictionByInstance[data?.instanceId];
      if (!restrictions) return { status: 'error', message: 'Connect instance not found' };

      const restriction = {
        instanceId: data.instanceId,
        restrictionId: data.restrictionId.trim(),
        restrictionName: data.restrictionName.trim(),
        description: data.description || '',
        status: data.status || 'Inactive',
        contactCriteria: data.contactCriteria,
        actions: data.actions,
        effectiveTime: data.effectiveTime
      };
      restrictions.push(restriction);
      return { status: 'ok', result: { restriction } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/routing-group-registry'),
    respond: async ({ data }) => {
      const routingGroups = mockFixtures.routingGroupsByInstance[data?.instanceId];
      if (!routingGroups) {
        return { status: 'error', message: 'Connect instance not found' };
      }

      const duplicate = routingGroups.some(
        (routingGroup) => (
          routingGroup.routingGroupId.toLowerCase() === data?.routingGroupId?.trim().toLowerCase() ||
          routingGroup.displayName.toLowerCase() === data?.displayName?.trim().toLowerCase()
        )
      );
      if (duplicate) {
        return { status: 'error', message: 'A routing group with this ID or name already exists' };
      }

      const routingGroup = {
        instanceId: data.instanceId,
        routingGroupId: data.routingGroupId.trim(),
        displayName: data.displayName.trim(),
        criteria: normalizeCriteria(data.criteria),
        connectQueueId: data.connectQueueId,
        active: data.active,
      };
      routingGroups.push(routingGroup);

      return { status: 'ok', result: { routingGroup } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/delete-routing-group/'),
    respond: async ({ data }) => {
      const routingGroups = mockFixtures.routingGroupsByInstance[data?.instanceId];
      if (!routingGroups) {
        return { status: 'error', message: 'Connect instance not found' };
      }

      const routingGroupIndex = routingGroups.findIndex(
        (rg) => rg.routingGroupId === data?.routingGroupId
      );

      if (routingGroupIndex === -1) {
        return { status: 'error', message: 'Routing group not found' };
      }

      // Soft delete: set deleted_at timestamp
      routingGroups[routingGroupIndex].deleted_at = new Date().toISOString();

      return { status: 'ok', result: { message: `Routing group '${data?.routingGroupId}' deleted successfully.` } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/delete-routing-policies/'),
    respond: async ({ data }) => {
      const routingPolicies = mockFixtures.routingPoliciesByInstance[data?.instanceId];
      if (!routingPolicies) {
        return { status: 'error', message: 'Connect instance not found' };
      }

      const policyIndex = routingPolicies.findIndex(
        (policy) => policy.policyId === data?.policyId
      );

      if (policyIndex === -1) {
        return { status: 'error', message: 'Routing policy not found' };
      }

      // Soft delete: set deleted_at timestamp
      routingPolicies[policyIndex].deleted_at = new Date().toISOString();

      return { status: 'ok', result: { message: `Routing policy '${data?.policyId}' deleted successfully.` } };
    }
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/delete-routing-restrictions/'),
    respond: async ({ data }) => {
      const routingRestrictions = mockFixtures.routingRestrictionByInstance[data?.instanceId];
      if (!routingRestrictions) {
        return { status: 'error', message: 'Connect instance not found' };
      }

      const restrictionIndex = routingRestrictions.findIndex(
        (restriction) => restriction.restrictionId === data?.restrictionId
      );

      if (restrictionIndex === -1) {
        return { status: 'error', message: 'Routing restriction not found' };
      }

      // Soft delete: set deleted_at timestamp
      routingRestrictions[restrictionIndex].deleted_at = new Date().toISOString();

      return { status: 'ok', result: { message: `Routing restriction '${data?.restrictionId}' deleted successfully.` } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/archive-data'),
     respond: async ({ data }) => {
        const archiveData = mockFixtures.archiveDataByInstance[data?.instanceId] || [];
        return {
          status: 'ok',
          result: {
            instanceId: data?.instanceId,
            archiveData
          }
        };
      },
  },
  {
    method: 'get',
    matches: (url) => url.includes('/admin/bulk-status'),
    respond: async ({ data }) => {
      const bulkStatus = mockFixtures.archiveBulkStatus || {};
      return { status: 'ok', job: bulkStatus };
    }
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/create-archive'),
    respond: async ({ data }) => {
      if (!data?.instanceId) {
        return { status: 'error', message: 'Missing required parameter: instanceId' };
      }
      const componentLabels = {
        agents: 'Agents',
        agentRoutingProfiles: 'Routing Profiles',
        queues: 'Queue',
        attributes: 'Attributes',
        securityProfiles: 'Security Profiles',
        hierarchyGroups: 'Hierarchy Groups',
        hoursOfOperation: 'Hours Of Operation',
        quickConnects: 'Quick Connects',
        contactFlows: 'Contact Flows',
        phoneNumbers: 'Phone Numbers',
        prompts: 'Prompts',
        flowModules: 'Flow Modules',
        // applicationRoutingPolicies: 'Application Routing Policies',
      };
      const archiveTypeComponents = {
        AGENTS_ONLY: ['agents'],
        ROUTING_ONLY: ['agentRoutingProfiles', 'queues', 'hoursOfOperation', 'quickConnects'],
        AGENTS_AND_ROUTING: ['agents', 'agentRoutingProfiles', 'queues', 'hoursOfOperation', 'quickConnects'],
      };
      const archiveType = data.archiveType || 'FULL';
      const components = archiveType === 'FULL'
        ? Object.keys(data.components || {}).filter((key) => data.components[key])
        : (archiveTypeComponents[archiveType] || []);
      const resourceCounts = components.map((component) => ({
        component,
        label: componentLabels[component] || component,
        count: Math.floor(Math.random() * 10) + 1,
      }));
      const resources = resourceCounts.reduce((acc, entry) => {
        acc[entry.component] = Array.from({ length: entry.count }, (_, i) => ({
          Id: `${entry.component}-${i + 1}`,
          Name: `Mock ${entry.label} ${i + 1}`,
        }));
        return acc;
      }, {});
      const archiveId = `archive-${Date.now()}`;
      const createdAt = new Date().toISOString();
      const archive = {
        archiveName: data.archiveName,
        instanceId: data.instanceId,
        instanceName: data.instanceName,
        archiveId,
        date: createdAt,
        status: 'Complete',
        archiveType,
        components,
        resourceCounts,
        resources: resourceCounts.reduce((sum, entry) => sum + entry.count, 0),
        document: {
          archiveId,
          instanceId: data.instanceId,
          instanceName: data.instanceName,
          archiveName: data.archiveName,
          archiveType,
          components,
          createdAt,
          resources,
        },
      };
      mockFixtures.archiveDataByInstance[data.instanceId] = [
        ...(mockFixtures.archiveDataByInstance[data.instanceId] || []),
        archive
      ];
      mockFixtures.archiveBulkStatus = {
        status: 'COMPLETED',
        job_type: 'ARCHIVE',
        result: {
          archive: archive
        }
      };
      return { status: 'ok', result: { archive } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/archive-detail'),
    respond: async ({ data }) => {
      const archives = mockFixtures.archiveDataByInstance[data?.instanceId] || [];
      const archive = archives.find((entry) => entry.archiveId === data?.archiveId);
      if (!archive) {
        return { status: 'error', message: 'Archive not found' };
      }
      // Mock fixtures may already be shaped like a full S3 document
      // (resources: {component: [...]}) rather than the DynamoDB
      // metadata item (resources: <count>). Prefer whichever is present.
      const resourcesIsMap = archive.resources && typeof archive.resources === 'object' && !Array.isArray(archive.resources);
      const document = archive.document || {
        archiveId: archive.archiveId,
        instanceId: archive.instanceId,
        instanceName: archive.instanceName,
        archiveName: archive.archiveName,
        archiveType: archive.archiveType,
        components: archive.components || (resourcesIsMap ? Object.keys(archive.resources) : []),
        createdAt: archive.date,
        resources: resourcesIsMap ? archive.resources : {},
      };
      return { status: 'ok', result: { archive, document } };
    },
  },
  {
    method: 'post',
    matches: (url) => url.includes('/admin/delete-archive'),
    respond: async ({ data }) => {
      const archives = mockFixtures.archiveDataByInstance[data?.instanceId] || [];
      const archiveIndex = archives.findIndex((entry) => entry.archiveId === data?.archiveId);
      if (archiveIndex === -1) {
        return { status: 'error', message: 'Archive not found' };
      }
      const [deletedArchive] = archives.splice(archiveIndex, 1);
      mockFixtures.archiveDataByInstance[data.instanceId] = archives;
      return { status: 'ok', result: { archive: deletedArchive } };
    },
  },
];

export const isMockApiEnabled = () => (
  process.env.REACT_APP_API_MODE === 'mock' ||
  process.env.REACT_APP_AUTH_MODE === 'bypass'
);

const findMockRoute = (method, url) => mockRoutes.find(
  (route) => route.method === method && route.matches(url)
);

const requestMock = async (method, url, config = {}) => {
  const route = findMockRoute(method, url);
  if (!route) {
    throw new Error(`No mock route configured for ${method.toUpperCase()} ${url}`);
  }

  await new Promise((resolve) => setTimeout(resolve, MOCK_DELAY_MS));
  return { data: await route.respond(config), status: 200, config };
};

export const get = (url, config) => (
  isMockApiEnabled() ? requestMock('get', url, config) : apiClient.get(url, config)
);

export const post = (url, data, config) => (
  isMockApiEnabled()
    ? requestMock('post', url, { ...config, data })
    : apiClient.post(url, data, config)
);

const apiService = { get, post, isMockApiEnabled };

export default apiService;
